import {
  getGetApiServiceRequestsQueryOptions,
  type ServiceRequestDto,
} from '@smartsash/api-client';
import { defineDataScreen, pagedQuery } from '@smartsash/data-screen';
import { usePermission } from '@smartsash/permissions';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { isAssignable } from './assignment/requestAssignment';
import MobileCard from './MobileCard';
import styles from './ServiceRequests.module.css';
import { ServiceRequestsArchiveToggle } from './ServiceRequestsArchiveToggle';
import { ServiceRequestsFilter, ServiceRequestsRequesterChip } from './ServiceRequestsFilter';
import { LazyRequestAssignee, LazyRowActions } from './serviceRequestsLazy';
import {
  serviceRequestColumnText,
  serviceRequestsQueryParams,
  serviceRequestsSearchSchema,
  type ServiceRequestColumnId,
} from './serviceRequestsModel';

import { useDataScreenTranslate } from '@/hooks/useDataScreenTranslate';
import {
  formatRequestDateTime,
  personName,
  typeLabelKey,
} from '@/ui/pages/ServiceRequestDetails/serviceRequestLabels';
import { StatusBadge } from '@/ui/pages/ServiceRequestDetails/StatusBadge';
import { EMPTY_VALUE } from '@/utils/emptyValue';

/** Headers are rendered, not built: the definition lives at module scope and cannot call `t`. */
function ColumnHeader({ column }: { column: ServiceRequestColumnId }) {
  const { t } = useTranslation();
  return <>{t(`serviceRequests.columns.${column}`)}</>;
}

function TypeCell({ row }: { row: ServiceRequestDto }) {
  const { t } = useTranslation();
  const key = typeLabelKey(row.type);
  return (
    <Link to="/requests/$id" params={{ id: row.id }} className={styles.link}>
      {key === undefined ? row.type : t(key)}
    </Link>
  );
}

/**
 * What the request is about (HS-1542 step 14). One line: a long subject ends in an ellipsis and
 * the full one is in the `title`; none reads as a muted "—".
 */
function SubjectCell({ row }: { row: ServiceRequestDto }) {
  const subject = serviceRequestColumnText.subject(row);
  if (subject === '') return <span className={styles['subject-empty']}>{EMPTY_VALUE}</span>;
  return (
    <span className={styles.subject} title={subject}>
      {subject}
    </span>
  );
}

function RequesterCell({ row }: { row: ServiceRequestDto }) {
  const { t } = useTranslation();
  const name = personName(row.requester);
  const email = row.requester.email;
  return (
    <div className={styles.person}>
      <span className={styles['person-name']}>{name ?? t('serviceRequests.unknownUser')}</span>
      {email != null && email !== '' && email !== name && (
        <span className={styles['person-email']}>{email}</span>
      )}
    </div>
  );
}

/**
 * Who works on the request (HS-1542). An open request offers a `serviceRequest.assign` caller the
 * assignee picker, loaded lazily with the text standing in meanwhile; anyone else, and every
 * closed request (which keeps its assignee), reads it as text. "Unassigned" is muted so it does
 * not read as a person's name. Rows are not clickable, so the picker needs no guard against a
 * row navigation.
 */
function AssigneeCell({ row }: { row: ServiceRequestDto }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const text =
    row.assignee == null ? (
      <span className={styles.unassigned}>{t('serviceRequests.assignee.unassigned')}</span>
    ) : (
      <span className={styles['person-name']}>
        {personName(row.assignee) ?? t('serviceRequests.unknownUser')}
      </span>
    );
  if (!isAssignable(row, can('serviceRequest.assign'))) return text;
  return (
    <Suspense fallback={text}>
      <LazyRequestAssignee request={row} />
    </Suspense>
  );
}

function CreatedCell({ row }: { row: ServiceRequestDto }) {
  const { i18n } = useTranslation();
  return <>{formatRequestDateTime(row.createdAt, i18n.language)}</>;
}

const columns: ColumnDef<ServiceRequestDto>[] = [
  {
    id: 'type',
    header: () => <ColumnHeader column="type" />,
    cell: ({ row }) => <TypeCell row={row.original} />,
    meta: { primary: true, value: serviceRequestColumnText.type },
  },
  // After the type: the two together tell open requests apart (HS-1542 step 14).
  {
    id: 'subject',
    header: () => <ColumnHeader column="subject" />,
    cell: ({ row }) => <SubjectCell row={row.original} />,
    meta: { width: '280px', value: serviceRequestColumnText.subject },
  },
  {
    id: 'requester',
    header: () => <ColumnHeader column="requester" />,
    cell: ({ row }) => <RequesterCell row={row.original} />,
    meta: { width: '240px', value: serviceRequestColumnText.requester },
  },
  {
    id: 'institution',
    header: () => <ColumnHeader column="institution" />,
    cell: ({ row }) => row.original.institutionName ?? EMPTY_VALUE,
    meta: { width: '200px', value: serviceRequestColumnText.institution },
  },
  {
    id: 'status',
    header: () => <ColumnHeader column="status" />,
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
    meta: { width: '140px', value: serviceRequestColumnText.status },
  },
  // After the status: together they say where the request stands and who has it (HS-1542).
  {
    id: 'assignee',
    header: () => <ColumnHeader column="assignee" />,
    cell: ({ row }) => <AssigneeCell row={row.original} />,
    meta: { width: '200px', value: serviceRequestColumnText.assignee },
  },
  {
    id: 'createdAt',
    header: () => <ColumnHeader column="createdAt" />,
    cell: ({ row }) => <CreatedCell row={row.original} />,
    meta: { width: '180px', value: serviceRequestColumnText.createdAt },
  },
];

/**
 * The admin Requests list (HS-1548, HS-1556) on `@smartsash/data-screen`.
 *
 * The endpoint takes no free-text search and no sort (it orders by `createdAt desc`), so the
 * search box is off and no column is sortable; `serviceRequestsQueryParams` also drops `q` and
 * `sort` if a stale link carries them, because any unknown key is a 400. Newest first is the
 * server's order.
 */
export const serviceRequestsScreen = defineDataScreen({
  entity: 'serviceRequest',
  searchSchema: serviceRequestsSearchSchema,
  useTranslate: useDataScreenTranslate,
  list: {
    // The generated query options keep the generated key, which server-ops invalidates after a
    // decision.
    query: (search) =>
      pagedQuery<ServiceRequestDto>(
        getGetApiServiceRequestsQueryOptions(serviceRequestsQueryParams(search)),
      ),
    columns,
    getRowId: (row) => row.id,
    searchable: false,
    mobileCard: (row) => <MobileCard row={row} />,
    titleKey: 'serviceRequests.title',
    // The Archived view has its own "none" copy; "none yet" would read as if nothing was sent.
    emptyState: (search) => ({
      titleKey:
        search.view === 'archived'
          ? 'serviceRequests.empty.archivedTitle'
          : 'serviceRequests.empty.title',
      descriptionKey: search.view === 'archived' ? undefined : 'serviceRequests.empty.description',
      filteredTitleKey: 'list.emptyFiltered.title',
      filteredDescriptionKey: 'list.emptyFiltered.description',
    }),
    rowActions: [
      {
        id: 'row-actions',
        render: (row) => (
          <Suspense fallback={null}>
            <LazyRowActions row={row} />
          </Suspense>
        ),
      },
    ],
    // The Archived toggle sits beside the funnel as a header control: `filterSlot` is a fixed
    // square for the funnel alone, and anything wider overflowed the card (HS-1548 round 2 QA).
    headerAction: {
      id: 'view',
      render: (search) => <ServiceRequestsArchiveToggle search={search} />,
    },
    filterSlot: () => <ServiceRequestsFilter />,
    // `Table` draws the notice row for any non-null node, so an unnarrowed list passes null.
    notice: (search) =>
      search.requesterUserId === undefined ? null : (
        <ServiceRequestsRequesterChip search={search} />
      ),
  },
});
